import { api } from '@/lib/api';
import type {
  WorkOrderCreateValues,
  WorkOrderUpdateValues,
} from './schema';
import type {
  WorkOrder,
  WorkOrderAssignment,
  WorkOrderEvent,
  WorkOrderStatus,
} from './types';

/**
 * WorkOrder REST client.
 *
 * Endpoints (Phase 3b backend):
 *   GET    /work-orders                  list (filters: vesselId, status, type)
 *   GET    /work-orders/:id              detail
 *   POST   /work-orders                  create (status starts at draft)
 *   PATCH  /work-orders/:id              update body (only when status=draft)
 *   DELETE /work-orders/:id              soft-delete
 *   POST   /work-orders/:id/transition   state machine transition (F5.4)
 *   POST   /work-orders/:id/assignments  assign user (F5.4)
 *   GET    /work-orders/:id/assignments  list assignees (F5.2)
 *   DELETE /work-orders/assignments/:assignmentId  remove (F5.4)
 *
 * Only the read/write surface is wired in F5.1; assignments and
 * transitions land alongside the detail page in later sub-phases.
 */

export interface ListWorkOrdersFilters {
  vesselId?: string;
  status?: WorkOrderStatus;
  type?: string;
  equipmentId?: string;
}

export const workOrdersApi = {
  list: async (filters: ListWorkOrdersFilters = {}): Promise<WorkOrder[]> => {
    const { data } = await api.get<WorkOrder[]>('/work-orders', {
      params: filters,
    });
    return data;
  },

  getById: async (id: string): Promise<WorkOrder> => {
    const { data } = await api.get<WorkOrder>(`/work-orders/${id}`);
    return data;
  },

  create: async (input: WorkOrderCreateValues): Promise<WorkOrder> => {
    const { data } = await api.post<WorkOrder>('/work-orders', input);
    return data;
  },

  update: async (
    id: string,
    patch: WorkOrderUpdateValues,
  ): Promise<WorkOrder> => {
    const { data } = await api.patch<WorkOrder>(`/work-orders/${id}`, patch);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/work-orders/${id}`);
  },

  /** State machine transition. Body matches the backend
   *  TransitionWorkOrderDto: newStatus is required, cancelReason is
   *  required when newStatus=cancelled, actualHours is optional and
   *  generally used when transitioning to pending_review.
   *
   *  This endpoint does NOT handle the cryptographic sign
   *  (pending_review → signed) — that goes through POST /:id/sign
   *  with the Ed25519 signature payload. See F5.4b. */
  transition: async (
    id: string,
    body: {
      newStatus: WorkOrderStatus;
      cancelReason?: string;
      actualHours?: number;
    },
  ): Promise<WorkOrder> => {
    const { data } = await api.post<WorkOrder>(
      `/work-orders/${id}/transition`,
      body,
    );
    return data;
  },

  /** Append-only timeline (Phase 3c). Comments + status logs +
   *  measurements + signatures + assignment logs, ordered ascending
   *  by createdAt server-side. */
  getEvents: async (id: string): Promise<WorkOrderEvent[]> => {
    const { data } = await api.get<WorkOrderEvent[]>(
      `/work-orders/${id}/events`,
    );
    return data;
  },

  /** Active + historical assignments for a WO. The list includes
   *  unassigned rows so the timeline can show "X was unassigned by Y". */
  getAssignments: async (id: string): Promise<WorkOrderAssignment[]> => {
    const { data } = await api.get<WorkOrderAssignment[]>(
      `/work-orders/${id}/assignments`,
    );
    return data;
  },

  /**
   * L16 — bulk actions. Both endpoints cap the request at 100 ids
   * server-side and return a partial-success envelope.
   *
   * Per-id failures (signed/closed/missing rows) populate `failedIds`
   * rather than aborting the batch — surface them to the user.
   */
  bulkUpdatePriority: async (input: {
    ids: string[];
    priority: 'low' | 'normal' | 'high' | 'critical';
  }): Promise<{ updatedIds: string[]; failedIds: string[] }> => {
    const { data } = await api.post<{
      updatedIds: string[];
      failedIds: string[];
    }>('/work-orders/bulk-update-priority', input);
    return data;
  },

  bulkDelete: async (input: {
    ids: string[];
  }): Promise<{ deletedIds: string[]; failedIds: string[] }> => {
    const { data } = await api.post<{
      deletedIds: string[];
      failedIds: string[];
    }>('/work-orders/bulk-delete', input);
    return data;
  },
};
